import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useLocation } from '@tanstack/react-router'
import { useMemo } from 'react'
import { alarmTableQuery, fleetQuery, type Door, type FleetPage, type Noise } from '../api/alarms'
import { moduleOf, shellQuery } from '../api/shell'
import { DataTable, useTableState } from '../ui/DataTable'
import { Content } from '../ui/Shell'
import { usePageStyles } from '../ui/usePageStyles'
import { AlarmDetails } from './AlarmDetails'
import { alarmColumns } from './columns'

/**
 * System Alarms, pe usa - macheta views/version-3/device_alarms/fleet.php,
 * element cu element: capul de pagina (softsee/adm/_pagehead.php) cu cele
 * patru cifre, cartonasul de introducere, cel de liniste cand nu suna nimic,
 * tabelul `#opteemumAlarms` (ProactiviteeAlarms.initDatatable) si zgomotul
 * din ultimele sapte zile.
 */

/** Foile pe care le cerea pagina veche: plugin-ul 'adm' si foaia ecranului. */
const STYLES = ['css/adm.css', 'css/device_alarms_fleet.css']

const TABLE_ID = 'opteemumAlarms'

function PageHead({ page }: { page: FleetPage }) {
  const stats = [
    { value: page.counters.critical, label: 'critical', tone: 'red-intense' },
    { value: page.counters.major, label: 'major', tone: 'yellow' },
    { value: page.counters.minor, label: 'minor', tone: 'green-mode' },
    { value: page.open, label: 'open now', tone: 'blue-hoki' },
  ]
  return (
    <div className={`adm-page-head adm-tone-${page.tone}`}>
      <span className="adm-page-icon">
        <i className={`fas ${page.icon}`}></i>
      </span>

      <div className="adm-page-title">
        <div className="adm-page-title-line">
          <h3>{page.title}</h3>
        </div>
        <span className="adm-page-note">{page.note}</span>
      </div>

      <div className="adm-page-stats">
        {stats.map((stat) => (
          <div className={`adm-stat adm-stat-${stat.tone}`} key={stat.label}>
            <span className="adm-stat-value">{stat.value}</span>
            <span className="adm-stat-label">{stat.label}</span>
          </div>
        ))}
      </div>

      {page.settings && (
        <div className="adm-page-actions">
          <a className="btn btn-sm btn-default" href={page.settings.url}>
            <i className="fas fa-sliders-h"></i> {page.settings.label}
          </a>
        </div>
      )}
    </div>
  )
}

/** Citirea unui semnal, cu aceleasi praguri ca in fleet.php. */
export function noiseReading(r: Noise['rows'][number]): string {
  if (r.flap_pct >= 50 && r.opened >= 4) return 'threshold too tight or no hysteresis'
  if (r.machines === 1 && r.opened >= 5) return 'one machine keeps tripping it - fix the machine, not the threshold'
  if (r.per_machine >= 5) return 'rings again and again on the same machines - recurring cause'
  if (r.still_open > 0 && r.still_open === r.opened) return 'all still open - nobody is closing these'
  return ''
}

export function noiseVerdict(flapPct: number): string {
  if (flapPct >= 40) return 'Nearly half the ringing was a blink: tighten the hysteresis or loosen the threshold on the signals below.'
  if (flapPct >= 20) return 'A fifth of the ringing was a blink - worth a look at the top signal.'
  return 'Little of it was a blink; what rang, rang for a reason.'
}

export function avgDuration(minutes: number | null): string {
  if (minutes === null) return '-'
  return minutes >= 120 ? `${Math.round(minutes / 60)} h` : `${Math.trunc(minutes)} min`
}

function NoiseCard({ noise }: { noise: Noise }) {
  const flapPct = Math.round((noise.flaps * 100) / Math.max(1, noise.total))
  const right = { textAlign: 'right' as const }
  return (
    <div className="adm-card da-noise" style={{ marginTop: 18 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13 }}>Noise, last {noise.days} days</strong>
        <span className="text-muted" style={{ fontSize: 12 }}>
          {noise.total} alarm{noise.total === 1 ? '' : 's'} opened on {noise.types} signal{noise.types === 1 ? '' : 's'}; {noise.flaps} ({flapPct}%) closed
          within {noise.flap_minutes} minutes - flaps. {noiseVerdict(flapPct)}
        </span>
      </div>
      <table className="table table-condensed" style={{ margin: '8px 0 0', fontSize: 12 }}>
        <thead>
          <tr>
            <th>Signal</th>
            <th style={right}>Opened</th>
            <th style={right}>Machines</th>
            <th style={right}>Per machine</th>
            <th style={right}>Flaps</th>
            <th style={right}>Avg. duration</th>
            <th>Reading</th>
          </tr>
        </thead>
        <tbody>
          {noise.rows.map((r) => (
            <tr key={r.type}>
              <td>{r.name}</td>
              <td style={right}>{r.opened}</td>
              <td style={right}>{r.machines}</td>
              <td style={right}>{r.per_machine}</td>
              <td style={r.flap_pct >= 50 ? { ...right, color: '#b42318', fontWeight: 600 } : right}>
                {r.flaps} ({r.flap_pct}%)
              </td>
              <td style={right}>{avgDuration(r.avg_minutes)}</td>
              <td className="text-muted">{noiseReading(r)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function AlarmFleetPage({ door }: { door: Door }) {
  const location = useLocation()
  const shell = useQuery(shellQuery(moduleOf(location.pathname)))
  const page = useQuery(fleetQuery(door))
  usePageStyles(STYLES, shell.data?.version ?? '')

  const unit = shell.data?.temperature ?? 'C'
  const columns = useMemo(() => alarmColumns(unit), [unit])
  const [state, setState] = useTableState(TABLE_ID, { start: 0, length: 25, order: { column: 5, dir: 'desc' }, search: '' })
  const orderColumn = columns.find((c) => c.index === state.order.column) ?? columns[5]

  const table = useQuery({
    ...alarmTableQuery({
      door,
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: orderColumn.index,
      orderName: orderColumn.name ?? '',
      orderDir: state.order.dir,
      search: state.search,
    }),
    placeholderData: keepPreviousData,
  })

  const data = page.data

  return (
    <Content>
      {data && <PageHead page={data} />}

      {data && (
        <div className="adm-card da-intro">
          <i className="fas fa-info-circle"></i> {data.intro}
        </div>
      )}

      {data && data.open === 0 && (
        <div className="adm-card da-quiet">
          Nothing is ringing on this door. That is not the same as nobody having armed the thresholds — open a machine and <strong>System Alarms</strong>{' '}
          to see what is watched.
        </div>
      )}

      <DataTable
        id={TABLE_ID}
        columnTools
        columns={columns}
        rows={(table.data?.data ?? []) as unknown as Record<string, unknown>[]}
        total={table.data?.recordsTotal ?? 0}
        fetching={table.isFetching}
        error={table.error ? table.error.message : null}
        onRetry={() => void table.refetch()}
        state={state}
        onState={setState}
        rowId={(row) => String(row.DT_RowId)}
        footerButtons={{ copy: true, colvis: true, refresh: () => void table.refetch() }}
        details={data ? (row) => <AlarmDetails door={door} id={Number(row.ID)} tabs={data.tabs} unit={unit} /> : undefined}
      />

      {data?.noise && data.noise.total > 0 && <NoiseCard noise={data.noise} />}
    </Content>
  )
}
